import React, { useEffect } from 'react';
import { Form, Row, Col, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { makeStyles } from "@material-ui/core/styles";
import { createTheme, ThemeProvider } from '@material-ui/core';
import InputTheme from './Helpers/InputTheme';
import SvgIcon from '@material-ui/core/SvgIcon';
import { SelectInput, StyledRadioInput } from '../../Helpers/Inputs';
import Notes from '../../../icons/Notes.svg';

const useStyles = makeStyles(theme => ({
  lightBackground: {
      backgroundColor: `${theme.palette.primary.main}16`,
  },
}));

export default function MedicalDetails(props) {

  const classes = useStyles();

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: MedicalDetails.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  const onSubmit = () => {
    props.onNext();
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'systolicBloodPressure' });
    setValue('systolicBloodPressure', props.formState.systolicBloodPressure);

    register({ name: 'diastolicBloodPressure' });
    setValue('diastolicBloodPressure', props.formState.diastolicBloodPressure);

    register({ name: 'bloodPressureMedication' });
    setValue('bloodPressureMedication', props.formState.bloodPressureMedication);

    register({ name: 'cholesterol' });
    setValue('cholesterol', props.formState.cholesterol);

    register({ name: 'hdlRatio' });
    setValue('hdlRatio', props.formState.hdlRatio);

    register({ name: 'cholesterolMedication' });
    setValue('cholesterolMedication', props.formState.cholesterolMedication);

    register({ name: 'diabetes' });
    setValue('diabetes', props.formState.diabetes);

    register({ name: 'ageOfDiagnosis' });
    setValue('ageOfDiagnosis', props.formState.ageOfDiagnosis);

    register({ name: 'a1cReading' });
    setValue('a1cReading', props.formState.a1cReading);

    register({ name: 'insulin' });
    setValue('insulin', props.formState.insulin);

    register({ name: 'hypertensionOrHighBloodPressure' });
    setValue('hypertensionOrHighBloodPressure', props.medicalHistory.hypertensionOrHighBloodPressure);

    register({ name: 'highCholesterol' });
    setValue('highCholesterol', props.medicalHistory.highCholesterol);

    register({ name: 'diabetesInfected' });
    setValue('diabetesInfected', props.medicalHistory.diabetes);

  }, [register]);

  useEffect(() => {
    if (props.medicalHistory.none === true) {
      props.onBack();
    }
  }, [])

  const onDiabetesChange = (field, value) => {
    var form = { ...props.fullFormState };
    form.pages.medicalDetails.ageOfDiagnosis = "";
    form.pages.medicalDetails.diabetes = value;
    props.onMultiFieldChange(form);
    setValue(field, value);
    setValue("ageOfDiagnosis", "");
    triggerValidation(field);
    triggerValidation("ageOfDiagnosis");
  }

  const getDiagnosisOptions = (type) => {
    if (type === "Type 1 Diabetes") {
      return [{ label: 'Under age 8', value: 'Under age 8' }, { label: 'Ages 8 – 15', value: 'Ages 8 – 15' }, { label: 'Ages 16 – 25', value: 'Ages 16 – 25' }, { label: 'Ages 26+', value: 'Ages 26+' }];
    }
    else if (type === "Type 2 Diabetes") {
      return [{ label: 'Under age 40', value: 'Under age 40' }, { label: 'Ages 40 – 50', value: 'Ages 40 – 50' }, { label: 'Ages 51 – 60', value: 'Ages 51 – 60' }, { label: 'Ages 61+', value: 'Ages 61+' }];
    }
    else {
      return [];
    }
  }

  return (
   
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <Container className="personalize-step">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="justify-content-center medical-details">
            <Col lg={5}>
              <div className="d-flex justify-content-center">
                <div className={`icon-container  ${classes.lightBackground}`}>
                  <SvgIcon component={Notes} color="primary" fontSize="large" />
                </div>
                
              </div>
              <div className="text-center medical-details-title">Additional Medical Details</div>
              {
                props.medicalHistory.hypertensionOrHighBloodPressure === true &&
                <QuestionSection title="High Blood Pressure">
                  <p className="medical-stat-title">My last blood pressure level was</p>
                  <Form.Group>
                    <SelectInput
                      name="systolicBloodPressure"
                      id="systolicBloodPressure"
                      value={props.formState.systolicBloodPressure}
                      onChange={(e) => handleValueChange('systolicBloodPressure', e.target.value)}
                      label="Systolic (top number)"
                      selectOptions={[{ label: 'Normal (130 or lower)', value: 'Normal (130 or lower)' }, { label: 'Elevated (131 – 140)', value: 'Elevated (131 – 140)' }, { label: 'Pre-Hypertension (141 – 145)', value: 'Pre-Hypertension (141 – 145)' }, { label: 'Hypertension (146 or higher)', value: 'Hypertension (146 or higher)' }]}
                      errors={errors.systolicBloodPressure}
                      inputRef={register}
                    />
                  </Form.Group>

                  <Form.Group>
                    <SelectInput
                      name="diastolicBloodPressure"
                      id="diastolicBloodPressure"
                      value={props.formState.diastolicBloodPressure}
                      onChange={(e) => handleValueChange('diastolicBloodPressure', e.target.value)}
                      label="Diastolic (bottom number)"
                      selectOptions={[{ label: 'Normal (80 or lower)', value: 'Normal (80 or lower)' }, { label: 'Elevated (81 – 85)', value: 'Elevated (81 – 85)' }, { label: 'Pre-Hypertension (86 – 90)', value: 'Pre-Hypertension (86 – 90)' }, { label: 'Hypertension (91 or higher)', value: 'Hypertension (91 or higher)' }]}
                      errors={errors.diastolicBloodPressure}
                      inputRef={register}
                    />
                  </Form.Group>

                  <p className="medical-stat-title">I am taking blood pressure medication.</p>
                  <Form.Group className="mb-0">
                    <StyledRadioInput
                      name="bloodPressureMedication"
                      id="bloodPressureMedication"
                      value={props.formState.bloodPressureMedication}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('bloodPressureMedication', e)}
                      errors={errors.bloodPressureMedication}
                      isBlankIcon={true}
                    />
                  </Form.Group>
                </QuestionSection>
              }
              {
                props.medicalHistory.highCholesterol === true &&
                <QuestionSection title="High Cholesterol">
                  <Form.Group>
                    <SelectInput
                      name="cholesterol"
                      id="cholesterol"
                      value={props.formState.cholesterol}
                      onChange={(e) => handleValueChange('cholesterol', e.target.value)}
                      label="My last cholesterol reading was"
                      selectOptions={[{ label: 'Normal (Less than 200)', value: 'Normal (Less than 200)' }, { label: 'Elevated (200-239)', value: 'Elevated (200-239)' }, { label: 'High (240 or Higher)', value: 'High (240 or Higher)' }]}
                      errors={errors.cholesterol}
                      inputRef={register}
                    />
                  </Form.Group>

                  <Form.Group>
                    <SelectInput
                      name="hdlRatio"
                      id="hdlRatio"
                      value={props.formState.hdlRatio}
                      onChange={(e) => handleValueChange('hdlRatio', e.target.value)}
                      label="My last HDL ratio was"
                      selectOptions={[{ label: 'Normal (4.5 or less)', value: 'Normal (4.5 or less)' }, { label: 'Elevated (4.6 to 5.5)', value: 'Elevated (4.6 to 5.5)' }, { label: 'At Risk (5.6 to 6.4)', value: 'At Risk (5.6 to 6.4)' }, { label: 'High (6.5 or higher)', value: 'High (6.5 or higher)' }]}
                      errors={errors.hdlRatio}
                      inputRef={register}
                    />
                  </Form.Group>

                  <p className="medical-stat-title">I am taking high cholesterol medication.</p>
                  <Form.Group className="mb-0">
                    <StyledRadioInput
                      name="cholesterolMedication"
                      id="cholesterolMedication"
                      value={props.formState.cholesterolMedication}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('cholesterolMedication', e)}
                      errors={errors.cholesterolMedication}
                      isBlankIcon={true}
                    />
                  </Form.Group>
                </QuestionSection>
              }
              {
                props.medicalHistory.diabetes === true &&
                <QuestionSection title="Diabetes">
                  <Form.Group>
                    <SelectInput
                      name="diabetes"
                      id="diabetes"
                      value={props.formState.diabetes}
                      onChange={(e) => onDiabetesChange('diabetes', e.target.value)}
                      label="I have"
                      selectOptions={[{ label: 'Type 1 Diabetes', value: 'Type 1 Diabetes' }, { label: 'Type 2 Diabetes', value: 'Type 2 Diabetes' }]}
                      errors={errors.diabetes}
                      inputRef={register}
                    />
                  </Form.Group>

                  <Form.Group>
                    <SelectInput
                      name="ageOfDiagnosis"
                      id="ageOfDiagnosis"
                      value={props.formState.ageOfDiagnosis}
                      onChange={(e) => handleValueChange('ageOfDiagnosis', e.target.value)}
                      label="I was diagnosed at"
                      selectOptions={getDiagnosisOptions(props.formState.diabetes)}
                      errors={errors.ageOfDiagnosis}
                      inputRef={register}
                      disabled={props.formState.diabetes === ""}
                      title={props.formState.diabetes === "" ? "You must select diabetes type to enable this option." : ""}
                    />
                  </Form.Group>

                  <Form.Group>
                    <SelectInput
                      name="a1cReading"
                      id="a1cReading"
                      value={props.formState.a1cReading}
                      onChange={(e) => handleValueChange('a1cReading', e.target.value)}
                      label="My last A1C reading was"
                      selectOptions={[{ label: 'Excellent (6.5 or less)', value: 'Excellent (6.5 or less)' }, { label: 'Average (6.6 to 7.5)', value: 'Average (6.6 to 7.5)' }, { label: 'Below Average (7.6 to 8.5) ', value: 'Below Average (7.6 to 8.5) ' }, { label: 'Uncontrolled (8.6 or higher)', value: 'Uncontrolled (8.6 or higher)' }]}
                      errors={errors.a1cReading}
                      inputRef={register}
                    />
                  </Form.Group>

                  <p className="medical-stat-title">I take insulin</p>
                  <Form.Group className="mb-0">
                    <StyledRadioInput
                      name="insulin"
                      id="insulin"
                      value={props.formState.insulin}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('insulin', e)}
                      errors={errors.insulin}
                      isBlankIcon={true}
                    />
                  </Form.Group>
                </QuestionSection>
              }
            </Col>
          </Row>
          <Row className="pre-quote-nav-control">
            <Col className="text-left">
              <Button variant="outlined" size="medium" color="primary" className="px-4" onClick={props.onBack}>BACK</Button>
            </Col>
            <Col className="text-right">
              <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">NEXT</Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </ThemeProvider>
  );
}

const QuestionSection = (props) => {
  const useStyles = makeStyles(theme => ({
    questionBox: {
      backgroundColor: 'white',
      borderRadius: '6px',
      boxShadow: '0px 1px 3px #00000033',
      padding: '1rem',
      paddingBottom: 0,
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#000000DB',
    }
  }));

  const classes = useStyles();
  return (
    <Row className="justify-content-center pt-3">
      <Col>
        <div className={`${classes.questionBox}`}>
          <p className={classes.sectionTitle}>{props.title}</p>
          {props.children}
        </div>
      </Col>
    </Row>
  )
}

MedicalDetails.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

MedicalDetails.ValidationSchema = object().shape({
  systolicBloodPressure: string().when('hypertensionOrHighBloodPressure', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  diastolicBloodPressure: string().when('hypertensionOrHighBloodPressure', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  bloodPressureMedication: string().when('hypertensionOrHighBloodPressure', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  cholesterol: string().when('highCholesterol', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  hdlRatio: string().when('highCholesterol', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  cholesterolMedication: string().when('highCholesterol', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  diabetes: string().when('diabetesInfected', {
    is: true,
    then: string().notRequired(),
    otherwise: string().notRequired()
  }),

  ageOfDiagnosis: string().when(['diabetes', 'diabetesInfected'], {
    is: (diabetes, diabetesInfected) => diabetesInfected === true && (diabetes === "Type 1 Diabetes" || diabetes === "Type 2 Diabetes"),
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  a1cReading: string().when('diabetesInfected', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  insulin: string().when('diabetesInfected', {
    is: true,
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),

});

MedicalDetails.DefaultFormState = (prefill) => {
  return {
    systolicBloodPressure: prefill ? 'Normal (130 or lower)' : '',
    diastolicBloodPressure: prefill ? 'Normal (80 or lower)' : '',
    bloodPressureMedication: prefill ? 'true' : '',
    cholesterol: prefill ? 'Normal (Less than 200)' : '',
    hdlRatio: prefill ? 'Normal (4.5 or less)' : '',
    cholesterolMedication: prefill ? 'true' : '',
    diabetes: prefill ? 'Type 1 Diabetes' : '',
    ageOfDiagnosis: prefill ? 'Under age 8' : '',
    a1cReading: prefill ? 'Excellent (6.5 or less)' : '',
    insulin: prefill ? 'true' : '',

  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/MedicalDetails.js